import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { CenterBox } from '@im/ui-lib/CenterBox';
import CircularProgress from '@mui/material/CircularProgress';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Stack from '@mui/material/Stack';
import { NavItem, useNavTo } from '../../../nav';

type Props = {
  closePopover: () => void;
  'data-testid'?: string;
  level: number;
  navItem: NavItem;
  showSubMenu: (navItem: NavItem, level: number, el: any) => void;
};

export function AvatarPopoverMenu(props: Readonly<Props>) {
  const {
    closePopover,
    'data-testid': testid = 'required',
    level,
    navItem,
    showSubMenu,
  } = props;
  const { navItems } = navItem;

  const [loading, setLoading] = useState(false);
  const [itemArr, setItemArr] = useState<NavItem[]>([]);
  const navTo = useNavTo();

  useEffect(() => {
    let active = true;
    if (Array.isArray(navItems)) {
      setItemArr(navItems);
    } else if (typeof navItems === 'function') {
      (async () => {
        setLoading(true);
        const arr = await navItems();
        if (active) {
          setItemArr(arr);
          setLoading(false);
        }
      })();
    }

    return () => {
      active = false;
    };
  }, [navItems]);

  return (
    <RootStack>
      {loading && (
        <CenterBox>
          <CircularProgress size={16} />
        </CenterBox>
      )}

      <ScrollArea>
        <MenuList>
          {itemArr.map((item) => {
            const hasItems = Boolean(item.navItems);
            const cursor = item.route || item.url ? 'pointer' : 'auto';

            return (
              <StyledMenuItem
                data-testid={`${testid}-${item.key}-CategoryName`}
                dense
                disableGutters
                key={item.key}
                onClick={() => {
                  closePopover();
                  if (item.route || item.url) {
                    navTo(item);
                  }
                }}
                onMouseEnter={
                  hasItems
                    ? (e) => showSubMenu(item, level, e.target)
                    : undefined
                }
                style={{ cursor: `${cursor}` }}
              >
                <ListItemText>{item.label ?? item.key}</ListItemText>

                {hasItems && (
                  <ListItemIcon>
                    <ArrowForwardIosIcon sx={{ height: '9px' }} />
                  </ListItemIcon>
                )}
              </StyledMenuItem>
            );
          })}
        </MenuList>
      </ScrollArea>
    </RootStack>
  );
}

const RootStack = styled(Stack)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 0,
}));

const ScrollArea = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflowX: 'hidden',
  overflowY: 'auto',
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  '& .MuiListItemIcon-root': {
    minWidth: 20,
  },
  paddingLeft: theme.spacing(2),
  width: 300,
}));
